import { useState, useEffect, useRef, Fragment, useCallback } from "react";
import {
  FaAngleLeft,
  FaBrain,
  FaEllipsis,
  FaAtom,
  FaStar,
  FaRegStarHalfStroke,
  FaCrown,
  FaMessage,
  FaTrash,
  FaPencil,
  FaCopy,
  FaThumbsUp,
  FaThumbsDown,
  FaArrowRotateLeft,
  FaItalic,
  FaChevronDown,
  FaPlay,
  FaWind,
  FaSpa,
  FaRegClock,
} from "react-icons/fa6";
import { IoSend } from "react-icons/io5";
import { toast } from "react-toastify";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  getTopMenuData,
  postAiPsychologist,
  getChatHistory,
  getChatHistoryBefore,
  getChatHistoryAdvanced,
  getAiModels,
  postDeleteChatAi,
  postDeleteChatFull,
  postEditMessage,
  postMessageFeedback,
  postRegenerateResponse,
  postDeleteMessage,
  getPractices,
} from "../../api/mainApi";
import Premium from "../premium/Premium";
import { useTranslation } from "react-i18next";
import ConfirmDialog from "../confirmDialog/ConfirmDialog";

const PRACTICE_TAG_REGEX =
  /\[practice\]\s*id=([^,]*),\s*type=([^,]*),\s*name=([^\]]*?)\s*\[\/practice\]/gi;
const PRACTICE_TAG_LOOSE = /\[practice\][^\]]*?\[\/practice\]/gi;

// Парсить усі [practice] блоки. Повертає валідні практики, текст без жодного
// тегу practice (валідного чи пошкодженого) та ознаку наявності пошкоджених тегів.
function parsePracticeBlocks(text) {
  const practices = [];
  let match;
  const strict = new RegExp(PRACTICE_TAG_REGEX.source, "gi");
  while ((match = strict.exec(text)) !== null) {
    const id = match[1].trim();
    const type = match[2].trim();
    const name = match[3].trim();
    if (id && type && name) {
      practices.push({ id, type, name });
    }
  }

  const rawMatches = text.match(PRACTICE_TAG_LOOSE);
  const hasMalformed = rawMatches ? rawMatches.length > practices.length : false;

  const cleanedText = text
    .replace(PRACTICE_TAG_LOOSE, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  return { practices, cleanedText, hasMalformed };
}

function AiPsychologist() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const testResult = location.state?.testResult;
  const [points, setPoints] = useState();
  const [text, setText] = useState(
    () => localStorage.getItem("aiPsychologistDraft") || ""
  ); // Стан для тексту
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false); // Стан для завантаження відповіді ШІ
  const [isChatLoading, setIsChatLoading] = useState(true); //Стан для завантаження історії
  const [isPointsLoading, setIsPointsLoading] = useState(true); //Стан для завантаження балів
  const messagesEndRef = useRef(null); // Реф для останнього повідомлення
  const chatContainerRef = useRef(null); // Реф для контейнера чату

  const [showPremium, setShowPremium] = useState(false);

  const [isUserPremium, setIsUserPremium] = useState(null);

  const maxRows = 6; // Максимальна кількість рядків
  const lineHeight = 24; // Висота одного рядка (в пікселях)
  const baseRows = 1; // Початкова кількість рядків
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(
    localStorage.getItem("selectedAiModel") || "Basic"
  );
  const [modelPrice, setModelPrice] = useState(
    localStorage.getItem("selectedAiModelPrice") || 10
  );
  const [isOpen, setIsOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  const [mode, setMode] = useState(null); // "ai" | "full" | null
  const [processing, setProcessing] = useState(false);

  // New state for message deletion confirmation
  const [deleteMessageMode, setDeleteMessageMode] = useState(null); // { messageId, messageText } | null
  const [deleteProcessing, setDeleteProcessing] = useState(false);

  // New states for message editing and context menu
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    messageId: null,
    messageText: "",
  });
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [touchStartTime, setTouchStartTime] = useState(0);
  const [longPressActive, setLongPressActive] = useState(null); // messageId being long pressed

  // New states for AI message reactions and variants
  const [messageReactions, setMessageReactions] = useState({});
  const [messageVariants, setMessageVariants] = useState({});
  const [currentVariantIndex, setCurrentVariantIndex] = useState({});

  // New state for text selection modal
  const [textSelectionModal, setTextSelectionModal] = useState({
    visible: false,
    messageId: null,
    messageText: "",
  });

  // New states for scroll to bottom button and auto scroll control
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // New state for AI response animation
  const [currentAiMessage, setCurrentAiMessage] = useState(null);

  // State to track if test result has been processed
  const [testResultProcessed, setTestResultProcessed] = useState(false);

  // Map of available practices by id (for AI-recommended practice blocks)
  const [practicesMap, setPracticesMap] = useState({});

  // Detect if device is mobile/tablet
  const isMobileDevice = () => {
    return (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      ) || window.innerWidth <= 768
    );
  };

  // Helper function to get current model object
  const getCurrentModel = () => {
    return models.find(([key]) => key === selectedModel)?.[1];
  };

  // Helper function to check if user can afford the action
  const canAffordAction = () => {
    const currentModel = getCurrentModel();
    return (
      points >= modelPrice || (isUserPremium && currentModel?.free_with_premium)
    );
  };

  const openDialog = (type) => setMode(type);
  const closeDialog = () => !processing && setMode(null);

  // Викликаємо потрібний запит після підтвердження
  const handleConfirm = useCallback(async () => {
    setProcessing(true);
    try {
      if (mode === "ai") {
        await postDeleteChatAi();
        toast.success(t("dialogs.delete_ai_success"));
      } else if (mode === "full") {
        await postDeleteChatFull();
        toast.success(t("dialogs.delete_full_success"));
        setMessages?.([]); // очищаємо лист
      }
      closeDialog();
    } catch (err) {
      console.error(err);
      toast.error(t("dialogs.delete_error"));
    } finally {
      setProcessing(false);
    }
  }, [mode, setMessages, t]);

  useEffect(() => {
    async function fetchModels() {
      const data = await getAiModels();
      if (!data?.aiModelsSnapshot) return;

      // Сортуємо моделі за якістю
      const sortedModels = Object.entries(data.aiModelsSnapshot).sort(
        (a, b) => a[1].quality - b[1].quality
      );
      setModels(sortedModels);

      if (!localStorage.getItem("selectedAiModel")) {
        const defaultModel = sortedModels[0][0]; // Ключ першої моделі
        const defaultPrice = sortedModels[0][1].price;

        setSelectedModel(defaultModel);
        setModelPrice(defaultPrice);

        localStorage.setItem("selectedAiModel", defaultModel);
        localStorage.setItem("selectedAiModelPrice", defaultPrice);
      } else {
        const storedModel = localStorage.getItem("selectedAiModel");
        if (storedModel && data.aiModelsSnapshot[storedModel]) {
          const price = data.aiModelsSnapshot[storedModel].price;

          setModelPrice(price);
          localStorage.setItem("selectedAiModelPrice", price);
        }
      }
    }

    fetchModels();
  }, []);

  // Завантажуємо практики (дихальні + медитації) для блоків від ШІ
  useEffect(() => {
    let cancelled = false;
    getPractices()
      .then((data) => {
        if (cancelled || !data) return;
        const map = {};
        ["breathing", "guided"].forEach((type) => {
          (data[type] || []).forEach((item) => {
            if (item && item.id !== undefined) map[item.id] = item;
          });
        });
        setPracticesMap(map);
      })
      .catch((err) => console.error("Помилка завантаження практик:", err));
    return () => {
      cancelled = true;
    };
  }, []);

  // Handle test result message
  useEffect(() => {
    if (
      testResult &&
      !isChatLoading &&
      initialLoadComplete &&
      !testResultProcessed
    ) {
      let formattedMessage = `[test-result]${t("psychologist.testCompleted", {
        testName: testResult.testName,
        score: testResult.score,
        maxScore: testResult.maxScore,
      })}`;

      if (
        testResult.subscaleScores &&
        Object.keys(testResult.subscaleScores).length > 0
      ) {
        formattedMessage += "\n\n" + t("psychologist.subscalesTitle") + ":";
        Object.keys(testResult.subscaleScores).forEach((subscaleId) => {
          const subscaleScore = testResult.subscaleScores[subscaleId];
          const subscaleMax = testResult.subscaleMaxScores[subscaleId];
          const subscaleName = testResult.subscaleNames[subscaleId];
          const subscaleMin = 0; // min score for subscales
          formattedMessage += `\n${subscaleName}: ${subscaleScore} ${t(
            "psychologist.pointsOf"
          )} ${subscaleMax}`;
        });
      }

      formattedMessage += "[/test-result]";

      // Add user message
      const newMessage = {
        sender: "user",
        text: formattedMessage,
        id: Date.now(),
      };
      setMessages((prevMessages) => [...prevMessages, newMessage]);

      // Create placeholder AI message
      const aiMessageId = Date.now() + 1;
      const placeholderAiMessage = {
        sender: "ai",
        text: "",
        id: aiMessageId,
        parentMessageId: newMessage.id,
        isLoading: true,
      };

      setCurrentAiMessage(aiMessageId);
      setMessages((prevMessages) => [...prevMessages, placeholderAiMessage]);
      setIsLoading(true);

      // Send to AI
      postAiPsychologist(
        formattedMessage,
        localStorage.getItem("selectedAiModel") || "Basic",
      )
        .then((data) => {
          setPoints(data.points);

          const aiMessage = {
            sender: "ai",
            text: data.reply || "No response from AI",
            id: aiMessageId,
            parentMessageId: newMessage.id,
            isLoading: false,
          };

          setMessages((prevMessages) =>
            prevMessages.map((msg) =>
              msg.id === aiMessageId ? aiMessage : msg
            )
          );

          setMessageVariants((prev) => ({
            ...prev,
            [aiMessage.id]: [data.reply || "No response from AI"],
          }));

          setCurrentVariantIndex((prev) => ({
            ...prev,
            [aiMessage.id]: 0,
          }));

          setCurrentAiMessage(null);

          // Sync message IDs with server
          setTimeout(() => {
            getChatHistoryAdvanced(null, 5)
              .then((history) => {
                setMessages((prevMessages) => {
                  const updated = prevMessages.map((localMsg) => {
                    const serverMsg = history.messages.find(
                      (sm) =>
                        sm.sender === localMsg.sender &&
                        sm.timestamp > Date.now() - 10000 &&
                        sm.text.trim() === localMsg.text.trim()
                    );
                    if (serverMsg) {
                      return serverMsg;
                    }
                    return localMsg;
                  });
                  localStorage.setItem("chatMessages", JSON.stringify(updated));
                  return updated;
                });
              })
              .catch((error) => {
                console.error("Error syncing message IDs:", error);
              });
          }, 1000);
        })
        .catch((error) => {
          console.error("Error sending test result:", error);

          const errorMessage = {
            sender: "ai",
            text: "An error occurred while generating the response.",
            id: aiMessageId,
            parentMessageId: newMessage.id,
            isLoading: false,
          };

          setMessages((prevMessages) =>
            prevMessages.map((msg) =>
              msg.id === aiMessageId ? errorMessage : msg
            )
          );

          setCurrentAiMessage(null);
        })
        .finally(() => {
          setIsLoading(false);
          setTestResultProcessed(true);
        });
    }
  }, [
    testResult,
    isChatLoading,
    initialLoadComplete,
    testResultProcessed,
  ]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelect = (key, premiumModel, price) => {
    if ((!isUserPremium && !premiumModel) || isUserPremium) {
      setSelectedModel(key);
      setModelPrice(price);
      localStorage.setItem("selectedAiModel", key);
      setIsOpen(false);
    } else {
      setShowPremium(true);
    }
  };

  const renderStars = (quality) => {
    const fullStars = Math.floor(quality);
    const halfStar = quality % 1 !== 0;
    return (
      <span className="flex items-center">
        {[...Array(fullStars)].map((_, i) => (
          <FaStar key={i} className="text-emerald-500 w-4 h-4" />
        ))}
        {halfStar && (
          <FaRegStarHalfStroke className="text-emerald-500 w-4 h-4" />
        )}
      </span>
    );
  };

  // Завантаження історії чату з localStorage та сервера
  useEffect(() => {
    localStorage.removeItem("chatMessages");
    const fetchTopMenuData = async () => {

      try {
        const data = await getTopMenuData();
        setPoints(data.points);
        setIsUserPremium(data.premium);

        if (!data.premium) {
          setSelectedModel("Basic");
          localStorage.setItem("selectedAiModel", "Basic");
        }
      } catch (error) {
        console.log("Помилка отримання TopMenuData:", error);
      } finally {
        setIsPointsLoading(false);
      }
    };

    const loadChatHistory = async () => {
      try {
        // 1️⃣ Спочатку беремо з localStorage
        const savedMessages =
          JSON.parse(localStorage.getItem("chatMessages")) || [];
        const lastKnownMessageId = savedMessages.length
          ? savedMessages[savedMessages.length - 1].id
          : null;

        console.log(`📦 localStorage: ${savedMessages.length} повідомлень`);

        // 2️⃣ Якщо є збережені повідомлення - відразу показуємо їх
        if (savedMessages.length > 0) {
          const processedMessages = processMessagesForDisplay(savedMessages);
          setMessages(processedMessages);
          setIsChatLoading(false);
          setInitialLoadComplete(true);

          // Scroll до низу після відображення
          setTimeout(() => scrollToBottom(), 100);
        }

        // 3️⃣ Потім запитуємо тільки НОВІ повідомлення з сервера
        try {
          const history = await getChatHistoryAdvanced(
            lastKnownMessageId, // Передаємо останній відомий ID
            50, // Limit для початкового завантаження
          );

          console.log(
            `🔄 Сервер: ${history.messages.length} нових повідомлень`
          );

          // 4️⃣ Додаємо тільки нові повідомлення (які відсутні в localStorage)
          const newMessages = history.messages.filter(
            (msg) => !savedMessages.some((savedMsg) => savedMsg.id === msg.id)
          );

          if (newMessages.length > 0) {
            const allMessages = [...savedMessages, ...newMessages];
            const processedMessages = processMessagesForDisplay(allMessages);

            setMessages(processedMessages);
            localStorage.setItem(
              "chatMessages",
              JSON.stringify(processedMessages)
            );

            console.log(`✅ Додано ${newMessages.length} нових повідомлень`);
          } else {
            console.log(`✅ Нових повідомлень немає`);
          }

          // Оновлюємо мета-інформацію з сервера
          if (history.messageReactions) {
            setMessageReactions(history.messageReactions);
          }
          if (history.messageVariants) {
            setMessageVariants(history.messageVariants);
          }
          if (history.currentVariantIndices) {
            setCurrentVariantIndex(history.currentVariantIndices);
          }
        } catch (serverError) {
          console.log("⚠️ Помилка завантаження з сервера:", serverError);

          // Якщо сервер недоступний, але є localStorage - працюємо офлайн
          if (savedMessages.length > 0) {
            console.log("✅ Працюємо з локальними даними");
          } else {
            // Fallback до базового API
            try {
              const history = await getChatHistory(
                lastKnownMessageId,
              );
              const processedMessages = processMessagesForDisplay(
                history.messages
              );
              setMessages(processedMessages);
              localStorage.setItem(
                "chatMessages",
                JSON.stringify(processedMessages)
              );
            } catch (fallbackError) {
              console.error("❌ Fallback API також не працює:", fallbackError);
            }
          }
        }
      } catch (error) {
        console.error("❌ Критична помилка завантаження історії:", error);
      } finally {
        setIsChatLoading(false);
        setInitialLoadComplete(true);
      }
    };

    fetchTopMenuData();
    loadChatHistory();
  }, []);

  // Функція для правильного групування повідомлень
  const processMessagesForDisplay = (rawMessages) => {
    const userMessages = [];
    const aiMessagesByParent = {};
    const standaloneAiMessages = []; // Для старих AI повідомлень без parentMessageId

    // Розділяємо user та AI повідомлення
    rawMessages.forEach((msg) => {
      if (msg.sender === "user") {
        userMessages.push(msg);
      } else if (msg.sender === "ai") {
        if (msg.parentMessageId !== undefined) {
          // Нові AI повідомлення з parentMessageId
          if (!aiMessagesByParent[msg.parentMessageId]) {
            aiMessagesByParent[msg.parentMessageId] = [];
          }
          aiMessagesByParent[msg.parentMessageId].push(msg);
        } else {
          // Старі AI повідомлення без parentMessageId
          standaloneAiMessages.push(msg);
        }
      }
    });

    // Сортуємо user повідомлення та standalone AI повідомлення за часом
    userMessages.sort((a, b) => (a.timestamp || a.id) - (b.timestamp || b.id));
    standaloneAiMessages.sort(
      (a, b) => (a.timestamp || a.id) - (b.timestamp || b.id)
    );

    // Об'єднуємо всі повідомлення та сортуємо їх за часом
    const allMessagesWithType = [
      ...userMessages.map((msg) => ({ ...msg, type: "user" })),
      ...standaloneAiMessages.map((msg) => ({ ...msg, type: "standalone_ai" })),
    ].sort((a, b) => (a.timestamp || a.id) - (b.timestamp || b.id));

    const finalMessages = [];
    let userMessageIndex = 0;

    allMessagesWithType.forEach((msg) => {
      if (msg.type === "user") {
        // Додаємо user повідомлення
        const userMsg = { ...msg };
        delete userMsg.type;
        finalMessages.push(userMsg);

        // Шукаємо AI відповіді для цього user повідомлення
        const aiResponses = aiMessagesByParent[userMsg.id];
        if (aiResponses && aiResponses.length > 0) {
          // Сортуємо AI відповіді за часом
          aiResponses.sort(
            (a, b) => (a.timestamp || a.id) - (b.timestamp || b.id)
          );

          // Знаходимо вибраний варіант або використовуємо перший
          const selectedVariant =
            aiResponses.find((resp) => resp.selected) || aiResponses[0];
          const selectedIndex = aiResponses.findIndex(
            (resp) => resp.id === selectedVariant.id
          );

          // Створюємо одне AI повідомлення зі всіма варіантами
          const consolidatedAiMessage = {
            ...selectedVariant,
            text: selectedVariant.text,
            variants: aiResponses.map((resp) => resp.text),
            currentVariantIndex: selectedIndex,
          };

          finalMessages.push(consolidatedAiMessage);

          // Оновлюємо стейт variants
          setMessageVariants((prev) => ({
            ...prev,
            [selectedVariant.id]: aiResponses.map((resp) => resp.text),
          }));

          // Оновлюємо індекс поточного варіанту
          setCurrentVariantIndex((prev) => ({
            ...prev,
            [selectedVariant.id]: selectedIndex,
          }));
        }
        userMessageIndex++;
      } else if (msg.type === "standalone_ai") {
        // Додаємо старі AI повідомлення як є
        const aiMsg = { ...msg };
        delete aiMsg.type;
        finalMessages.push(aiMsg);

        // Ініціалізуємо варіанти для старих повідомлень
        setMessageVariants((prev) => ({
          ...prev,
          [aiMsg.id]: [aiMsg.text],
        }));

        setCurrentVariantIndex((prev) => ({
          ...prev,
          [aiMsg.id]: 0,
        }));
      }
    });

    return finalMessages;
  };

  // Функція для обчислення рядків тексту
  const calculateRows = (value) => {
    const lines = value.split("\n").length; // Кількість рядків у тексті
    const scrollRows = Math.ceil(value.length / 40); // Кількість рядків по ширині (40 символів на рядок)
    return Math.min(maxRows, Math.max(baseRows, Math.max(lines, scrollRows)));
  };

  const currentRows = calculateRows(text);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Check if user is near bottom of chat
  const checkScrollPosition = () => {
    if (!chatContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    const scrollBottom = scrollHeight - scrollTop - clientHeight;

    // Show scroll button if user scrolled up more than 2-3 message heights
    setShowScrollButton(scrollBottom > 200);

    // Enable auto scroll if user is near bottom
    setShouldAutoScroll(scrollBottom < 100);
  };

  const handleSendMessage = async () => {
    if (text.trim().length === 0 || isLoading) return;
    if (!canAffordAction()) return setShowPremium(true);

    const userMessageTimestamp = Date.now();
    const temporaryUserId = `temp_user_${userMessageTimestamp}`;
    const temporaryAiId = `temp_ai_${userMessageTimestamp + 1}`;

    // 1️⃣ Створюємо тимчасові повідомлення з placeholder ID
    const newUserMessage = {
      sender: "user",
      text: text,
      id: temporaryUserId,
      timestamp: userMessageTimestamp,
      isPending: true, // Позначка що ID ще тимчасовий
    };

    const placeholderAiMessage = {
      sender: "ai",
      text: "",
      id: temporaryAiId,
      parentMessageId: temporaryUserId,
      timestamp: userMessageTimestamp + 1,
      isLoading: true,
      isPending: true,
    };

    // 2️⃣ ОДРАЗУ додаємо в UI
    setMessages((prev) => [...prev, newUserMessage, placeholderAiMessage]);
    setText("");
    setIsLoading(true);
    setCurrentAiMessage(temporaryAiId);

    // 3️⃣ Зберігаємо в localStorage (з тимчасовими ID)
    const messagesForStorage = [
      ...messages,
      newUserMessage,
      placeholderAiMessage,
    ];
    localStorage.setItem("chatMessages", JSON.stringify(messagesForStorage));

    try {
      // 4️⃣ Відправляємо на сервер
      const data = await postAiPsychologist(
        newUserMessage.text,
        localStorage.getItem("selectedAiModel") || "Basic",
      );

      // 5️⃣ Отримуємо реальні ID від сервера
      const { reply, points, userMessageId, aiMessageId } = data;

      localStorage.removeItem("aiPsychologistDraft");

      await setPoints(points);

      // 6️⃣ Оновлюємо повідомлення з реальними ID від сервера
      setMessages((prevMessages) => {
        const updated = prevMessages.map((msg) => {
          if (msg.id === temporaryUserId) {
            // Оновлюємо user повідомлення
            return {
              ...msg,
              id: userMessageId,
              isPending: false,
            };
          }
          if (msg.id === temporaryAiId) {
            // Оновлюємо AI повідомлення
            return {
              ...msg,
              id: aiMessageId,
              text: reply || "No response from AI",
              parentMessageId: userMessageId,
              isLoading: false,
              isPending: false,
            };
          }
          return msg;
        });

        // 7️⃣ Зберігаємо в localStorage з реальними ID
        localStorage.setItem("chatMessages", JSON.stringify(updated));
        return updated;
      });

      // 8️⃣ Ініціалізуємо варіанти для нового AI повідомлення
      setMessageVariants((prev) => ({
        ...prev,
        [aiMessageId]: [reply || "No response from AI"],
      }));

      setCurrentVariantIndex((prev) => ({
        ...prev,
        [aiMessageId]: 0,
      }));

      setCurrentAiMessage(null);

      // ✅ ВИДАЛИЛИ надлишковий запит синхронізації!
      // Більше не треба getChatHistoryAdvanced() після відправки
      console.log("✅ Повідомлення відправлено з реальними ID");
    } catch (error) {
      console.error("❌ Помилка відправки:", error);

      setText(newUserMessage.text);

      // У разі помилки оновлюємо AI повідомлення з помилкою
      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.id === temporaryAiId
            ? {
                ...msg,
                text:
                  error.response?.data?.error ||
                  "An error occurred while generating the response.",
                isLoading: false,
                isPending: false,
              }
            : msg
        )
      );

      setCurrentAiMessage(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleScroll = async () => {
    if (!chatContainerRef.current || isLoading) return;

    checkScrollPosition();

    const { scrollTop } = chatContainerRef.current;

    // Якщо користувач в самому верху
    if (scrollTop === 0) {
      const firstMessage = messages[0];
      if (!firstMessage || firstMessage.id <= 0) return;

      // Перевіряємо чи не завантажуємо вже
      if (chatContainerRef.current.dataset.loading === "true") return;

      try {
        chatContainerRef.current.dataset.loading = "true";

        console.log(
          `📜 Завантаження старіших повідомлень до ${firstMessage.id}`
        );

        // Використовуємо новий endpoint для завантаження старіших повідомлень
        const history = await getChatHistoryBefore(
          firstMessage.id,
          30, // Завантажуємо по 30 повідомлень за раз
        );

        if (history.messages.length > 0) {
          // Фільтруємо дублікати
          const newMessages = history.messages.filter(
            (msg) => !messages.some((existingMsg) => existingMsg.id === msg.id)
          );

          if (newMessages.length > 0) {
            // Зберігаємо поточну позицію скролу
            const previousScrollHeight = chatContainerRef.current.scrollHeight;

            // Додаємо нові повідомлення ЗВЕРХУ
            setMessages((prev) => [...newMessages, ...prev]);

            // Оновлюємо localStorage
            const allMessages = [...newMessages, ...messages];
            localStorage.setItem("chatMessages", JSON.stringify(allMessages));

            // Відновлюємо позицію скролу після додавання
            setTimeout(() => {
              const newScrollHeight = chatContainerRef.current.scrollHeight;
              const scrollDifference = newScrollHeight - previousScrollHeight;
              chatContainerRef.current.scrollTop = scrollDifference;
            }, 0);

            console.log(
              `✅ Завантажено ${newMessages.length} старіших повідомлень`
            );
          } else {
            console.log(`ℹ️ Нових повідомлень немає (всі вже завантажені)`);
          }
        } else {
          console.log(`ℹ️ Досягнуто початку історії`);
        }
      } catch (error) {
        console.error("❌ Помилка завантаження старіших повідомлень:", error);
      } finally {
        chatContainerRef.current.dataset.loading = "false";
      }
    }
  };

  useEffect(() => {
    if (shouldAutoScroll && initialLoadComplete) {
      scrollToBottom();
    }
  }, [messages, shouldAutoScroll, initialLoadComplete]);

  // New functions for message editing and context menu
  const handleEditMessage = (messageId, currentText) => {
    setEditingMessageId(messageId);
    setEditingText(currentText);
    setContextMenu({
      visible: false,
      x: 0,
      y: 0,
      messageId: null,
      messageText: "",
    });
  };

  const handleSaveEdit = async () => {
    // Перевірка: текст не пустий та відрізняється від оригінального
    if (editingText.trim().length === 0) return;

    // Знаходимо оригінальне повідомлення для порівняння
    const originalMessage = messages.find((msg) => msg.id === editingMessageId);
    if (
      !originalMessage ||
      editingText.trim() === originalMessage.text.trim()
    ) {
      return; // Текст не змінився
    }

    // Знаходимо індекс повідомлення
    const messageIndex = messages.findIndex(
      (msg) => msg.id === editingMessageId
    );
    if (messageIndex === -1) return;

    // ОДРАЗУ оновлюємо повідомлення локально
    const updatedMessages = [...messages];
    updatedMessages[messageIndex] = {
      ...updatedMessages[messageIndex],
      text: editingText,
    };

    // Якщо є AI відповідь відразу після редагованого повідомлення, видаляємо її
    if (
      messageIndex + 1 < updatedMessages.length &&
      updatedMessages[messageIndex + 1].sender === "ai"
    ) {
      updatedMessages.splice(messageIndex + 1, 1);
    }

    // Додаємо повідомлення з анімацією завантаження
    const loadingMessage = {
      id: `loading-${Date.now()}`,
      text: "",
      sender: "ai",
      timestamp: Date.now(),
      isLoading: true,
    };
    updatedMessages.splice(messageIndex + 1, 0, loadingMessage);

    // Оновлюємо стан одразу
    setMessages(updatedMessages);
    setEditingMessageId(null);
    setEditingText("");

    try {
      // Викликаємо API для редагування повідомлення
      const response = await postEditMessage(
        editingMessageId,
        editingText,
      );

      // Деструктуризуємо відповідь від API
      const { reply, points, aiMessageId, editedMessageId } = response;

      // Оновлюємо повідомлення з новими даними від сервера
      const finalMessages = [...updatedMessages];

      // Оновлюємо ID редагованого повідомлення якщо потрібно
      if (editedMessageId && editedMessageId !== editingMessageId) {
        finalMessages[messageIndex].id = editedMessageId;
      }

      // Замінюємо loading повідомлення на реальну AI відповідь
      const loadingIndex = finalMessages.findIndex((msg) => msg.isLoading);
      if (loadingIndex !== -1) {
        if (reply) {
          finalMessages[loadingIndex] = {
            id: aiMessageId,
            text: reply,
            sender: "ai",
            timestamp: Date.now(),
            points: points || 0,
            isLoading: false,
          };
        } else {
          // Якщо немає відповіді, видаляємо loading повідомлення
          finalMessages.splice(loadingIndex, 1);
        }
      }

      // Оновлюємо локальний стан
      setMessages(finalMessages);

      // Зберігаємо в localStorage
      localStorage.setItem("chatMessages", JSON.stringify(finalMessages));

      toast.success(t("psychologist.message_edited"));
    } catch (error) {
      console.error("Error editing message:", error);
      toast.error(t("psychologist.edit_failed"));

      // У разі помилки видаляємо loading повідомлення та повертаємо оригінальний текст
      const errorMessages = [...messages];
      const loadingIndex = errorMessages.findIndex((msg) => msg.isLoading);
      if (loadingIndex !== -1) {
        errorMessages.splice(loadingIndex, 1);
      }
      // Повертаємо оригінальний текст
      errorMessages[messageIndex].text = originalMessage.text;

      setMessages(errorMessages);
      setEditingMessageId(editingMessageId);
      setEditingText(originalMessage.text);
    }
  };

  const isSaveButtonActive = () => {
    // Перевіряємо чи текст не пустий
    if (editingText.trim().length === 0) return false;

    // Знаходимо оригінальне повідомлення
    const originalMessage = messages.find((msg) => msg.id === editingMessageId);
    if (!originalMessage) return false;

    // Перевіряємо чи текст відрізняється від оригінального
    return editingText.trim() !== originalMessage.text.trim();
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditingText("");
  };

  const handleCopyMessage = (messageId, text) => {
    // Get the current variant text if this is an AI message
    const message = messages.find((msg) => msg.id === messageId);
    const textToCopy =
      message && message.sender === "ai"
        ? getCurrentVariantText(messageId)
        : text;

    // Try modern clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(textToCopy)
        .then(() => {
          // Success - no toast needed
        })
        .catch(() => {
          // Fallback to older method
          fallbackCopyTextToClipboard(textToCopy);
        });
    } else {
      // Fallback for older browsers
      fallbackCopyTextToClipboard(textToCopy);
    }
    setContextMenu({
      visible: false,
      x: 0,
      y: 0,
      messageId: null,
      messageText: "",
    });
  };

  const fallbackCopyTextToClipboard = (text) => {
    // Create a temporary textarea element
    const textArea = document.createElement("textarea");
    textArea.value = text;

    // Make it invisible but selectable
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    textArea.style.opacity = "0";

    document.body.appendChild(textArea);

    try {
      // Select and copy
      textArea.focus();
      textArea.select();

      const successful = document.execCommand("copy");
      if (successful) {
        toast.success(t("psychologist.message_copied"));
      } else {
        toast.error(t("psychologist.copy_failed"));
      }
    } catch (err) {
      console.error("Fallback copy failed:", err);
      toast.error(t("psychologist.copy_failed"));
    } finally {
      // Clean up
      document.body.removeChild(textArea);
    }
  };

  const handleDeleteMessage = (messageId) => {
    const message = messages.find((msg) => msg.id === messageId);
    if (message) {
      setDeleteMessageMode({
        messageId,
        messageText: message.text,
      });
      setContextMenu({
        visible: false,
        x: 0,
        y: 0,
        messageId: null,
        messageText: "",
      });
    }
  };

  const handleConfirmDeleteMessage = useCallback(async () => {
    if (!deleteMessageMode) return;

    setDeleteProcessing(true);
    try {
      const { messageId } = deleteMessageMode;

      // Call API to delete message
      await postDeleteMessage(messageId);

      // Find the message and its response (if it's a user message)
      const messageIndex = messages.findIndex((msg) => msg.id === messageId);
      if (messageIndex === -1) return;

      const message = messages[messageIndex];
      let messagesToDelete = [messageId];

      // If it's a user message, also delete the AI response
      if (message.sender === "user" && messageIndex < messages.length - 1) {
        const nextMessage = messages[messageIndex + 1];
        if (nextMessage.sender === "ai") {
          messagesToDelete.push(nextMessage.id);
        }
      }

      // Remove messages
      const updatedMessages = messages.filter(
        (msg) => !messagesToDelete.includes(msg.id)
      );
      setMessages(updatedMessages);

      // Save to localStorage
      localStorage.setItem("chatMessages", JSON.stringify(updatedMessages));

      setDeleteMessageMode(null);
      toast.success(t("psychologist.message_deleted"));
    } catch (error) {
      console.error("Error deleting message:", error);
      toast.error(t("psychologist.delete_failed"));
    } finally {
      setDeleteProcessing(false);
    }
  }, [deleteMessageMode, messages, t]);

  const closeDeleteMessageDialog = () => {
    if (!deleteProcessing) {
      setDeleteMessageMode(null);
    }
  };

  // Touch and mouse event handlers for context menu
  const handleTouchStart = (e, messageId, messageText) => {
    // Don't show context menu for messages being edited
    if (editingMessageId === messageId) return;

    setTouchStartTime(Date.now());
    setLongPressActive(messageId);
    const timer = setTimeout(() => {
      // Only disable text selection when context menu is actually shown
      if (e.currentTarget) {
        e.currentTarget.style.userSelect = "none";
        e.currentTarget.style.webkitUserSelect = "none";
        e.currentTarget.style.MozUserSelect = "none";
        e.currentTarget.style.msUserSelect = "none";
      }
      showContextMenu(e, messageId, messageText);
      setLongPressActive(null);
    }, 700); // Long press duration
    setLongPressTimer(timer);
  };

  const handleTouchEnd = (e) => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
    setLongPressActive(null);
  };

  const handleTouchMove = (e) => {
    // If user moves finger during long press, cancel the timer
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
    setLongPressActive(null);
  };

  const handleMouseDown = (e, messageId, messageText) => {
    // Don't show context menu for messages being edited
    if (editingMessageId === messageId) return;

    if (e.button === 2) {
      // Right click
      e.preventDefault();
      showContextMenu(e, messageId, messageText);
    }
  };

  const showContextMenu = (e, messageId, messageText) => {
    e.preventDefault();
    let x, y;

    if (e.clientX !== undefined && e.clientY !== undefined) {
      // Mouse event or touch event with client coordinates
      x = e.clientX;
      y = e.clientY;
    } else if (e.touches && e.touches[0]) {
      // Touch event
      x = e.touches[0].clientX;
      y = e.touches[0].clientY;
    } else if (e.currentTarget) {
      // Fallback to element position
      const rect = e.currentTarget.getBoundingClientRect();
      x = rect.left + rect.width / 2;
      y = rect.top;
    } else {
      // Default position
      x = window.innerWidth / 2;
      y = window.innerHeight / 2;
    }

    setContextMenu({
      visible: true,
      x: x,
      y: y,
      messageId,
      messageText,
    });
  };

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contextMenu.visible) {
        setContextMenu({
          visible: false,
          x: 0,
          y: 0,
          messageId: null,
          messageText: "",
        });
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [contextMenu.visible]);

  function highlightHeadings(text) {
    // Розбиваємо текст на рядки
    const lines = text.split("\n");

    return lines.map((line, index) => {
      // Регулярний вираз: починається з цифр, крапка, пробіл, текст і закінчується двокрапкою, потім решта рядка
      const headingRegex = /^(\d+\.\s.*?:)(.*)$/;
      const match = line.match(headingRegex);

      if (match) {
        // match[1] — це заголовок, match[2] — решта рядка
        return (
          <Fragment key={index}>
            <strong>{match[1]}</strong>
            {match[2]}
            {index !== lines.length - 1 ? "\n" : null}
          </Fragment>
        );
      } else {
        return (
          <Fragment key={index}>
            {line}
            {index !== lines.length - 1 ? "\n" : null}
          </Fragment>
        );
      }
    });
  }

  // Get the last user message ID
  const getLastUserMessageId = () => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].sender === "user") {
        return messages[i].id;
      }
    }
    return null;
  };

  // Get the last AI message ID
  const getLastAiMessageId = () => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].sender === "ai") {
        return messages[i].id;
      }
    }
    return null;
  };

  const lastUserMessageId = getLastUserMessageId();
  const lastAiMessageId = getLastAiMessageId();

  // Handle AI message reactions (like/dislike)
  const handleMessageReaction = async (messageId, reaction) => {
    try {
      // Call API to send feedback
      await postMessageFeedback(messageId, reaction);

      // Update local state
      setMessageReactions((prev) => ({
        ...prev,
        [messageId]: reaction,
      }));

      // Success - no toast needed for reactions
    } catch (error) {
      console.error("Error sending feedback:", error);
      toast.error(t("psychologist.feedback_failed"));
    }
  };

  // Handle regenerating AI response
  const handleRegenerateResponse = async (messageId) => {
    if (isLoading) return;

    if (!canAffordAction()) {
      setShowPremium(true);
      return;
    }

    setIsLoading(true);

    // Тимчасовий ID для нового варіанту
    const temporaryNewAiId = `temp_regen_${Date.now()}`;

    // Встановлюємо loading state
    setMessages((prevMessages) =>
      prevMessages.map((msg) =>
        msg.id === messageId ? { ...msg, isLoading: true } : msg
      )
    );

    try {
      // Викликаємо API
      const data = await postRegenerateResponse();
      const { reply, points: newPoints, aiMessageId } = data;

      await setPoints(newPoints);

      // Отримуємо поточні варіанти
      const currentVariants = messageVariants[messageId] || [
        messages.find((msg) => msg.id === messageId)?.text || "",
      ];

      // Додаємо новий варіант
      const newVariants = [...currentVariants, reply || "No response from AI"];
      const newIndex = newVariants.length - 1;

      // Оновлюємо стейт варіантів
      setMessageVariants((prev) => ({
        ...prev,
        [messageId]: newVariants,
        // Якщо API повернув новий ID, копіюємо варіанти на новий ID
        ...(aiMessageId && aiMessageId !== messageId
          ? { [aiMessageId]: newVariants }
          : {}),
      }));

      setCurrentVariantIndex((prev) => ({
        ...prev,
        [messageId]: newIndex,
        ...(aiMessageId && aiMessageId !== messageId
          ? { [aiMessageId]: newIndex }
          : {}),
      }));

      // Оновлюємо повідомлення
      setMessages((prevMessages) => {
        const updated = prevMessages.map((msg) =>
          msg.id === messageId
            ? {
                ...msg,
                id: aiMessageId || messageId, // Використовуємо новий ID від API
                text: newVariants[newIndex],
                isLoading: false,
              }
            : msg
        );

        localStorage.setItem("chatMessages", JSON.stringify(updated));
        return updated;
      });

      console.log("✅ Відповідь регенерована з реальним ID:", aiMessageId);
    } catch (error) {
      console.error("❌ Помилка регенерації:", error);

      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.id === messageId ? { ...msg, isLoading: false } : msg
        )
      );

      toast.error(t("psychologist.regeneration_failed"));
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to get the current variant text for a message
  const getCurrentVariantText = (messageId) => {
    const variants = messageVariants[messageId];
    const currentIndex = currentVariantIndex[messageId] || 0;

    if (variants && variants.length > 0 && variants[currentIndex]) {
      return variants[currentIndex];
    }

    // Fallback to original message text
    const message = messages.find((msg) => msg.id === messageId);
    return message?.text || "";
  };

  // Обробник кліку по блоку практики від ШІ
  const handleStartPractice = (practice) => {
    const practiceData = practicesMap[practice.id];
    if (!practiceData) {
      toast.error(t("psychologist.practiceNotFound"));
      return;
    }
    if (practiceData.isPremium && !isUserPremium) {
      setShowPremium(true);
      return;
    }
    navigate(`/meditation-player/${practice.id}`, {
      state: { practiceData },
    });
  };

  return (
    <div className="flex flex-col h-dvh -my-4">
      {mode && (
        <ConfirmDialog
          status="warning"
          title={
            mode === "ai"
              ? "dialogs.delete_ai_title"
              : "dialogs.delete_full_title"
          }
          message={
            mode === "ai"
              ? "dialogs.delete_ai_message"
              : "dialogs.delete_full_message"
          }
          confirmText={t("common.delete")}
          cancelText={t("common.cancel")}
          onConfirm={handleConfirm}
          onCancel={closeDialog}
          processing={processing}
        />
      )}
      {deleteMessageMode && (
        <ConfirmDialog
          status="warning"
          title="dialogs.delete_message_title"
          message="dialogs.delete_message_message"
          confirmText={t("common.delete")}
          cancelText={t("common.cancel")}
          onConfirm={handleConfirmDeleteMessage}
          onCancel={closeDeleteMessageDialog}
          processing={deleteProcessing}
        />
      )}
      {showPremium && (
        <Premium showPremium={showPremium} setShowPremium={setShowPremium} />
      )}

      {/* Text Selection Modal */}
      {textSelectionModal.visible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-lg w-full max-w-2xl max-h-[80vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-700">
              <h3 className="text-lg font-semibold text-white">
                {t("psychologist.select_text")}
              </h3>
              <button
                onClick={() =>
                  setTextSelectionModal({
                    visible: false,
                    messageId: null,
                    messageText: "",
                  })
                }
                className="text-gray-400 hover:text-white p-1"
              >
                <FaAngleLeft className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 p-4 overflow-y-auto">
              <div
                className="text-white whitespace-pre-wrap select-text cursor-text text-base leading-relaxed"
                style={{
                  userSelect: "text",
                  WebkitUserSelect: "text",
                  MozUserSelect: "text",
                  msUserSelect: "text",
                }}
              >
                {highlightHeadings(textSelectionModal.messageText)}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 p-4 border-t border-gray-700">
              <button
                onClick={() => {
                  handleCopyMessage(
                    textSelectionModal.messageId,
                    textSelectionModal.messageText
                  );
                  setTextSelectionModal({
                    visible: false,
                    messageId: null,
                    messageText: "",
                  });
                }}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
              >
                <FaCopy className="w-4 h-4" />
                {t("psychologist.copy")}
              </button>
              <button
                onClick={() =>
                  setTextSelectionModal({
                    visible: false,
                    messageId: null,
                    messageText: "",
                  })
                }
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                {t("common.close")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Context Menu */}
      {contextMenu.visible && (
        <div
          className="fixed bg-slate-800 border border-slate-600 rounded-lg shadow-lg z-50 py-2 min-w-[180px]"
          style={{
            left: `${Math.min(contextMenu.x, window.innerWidth - 190)}px`,
            top: `${Math.min(contextMenu.y, window.innerHeight - 100)}px`,
          }}
        >
          {!contextMenu.messageText.includes("[test-result]") && (
            <>
              <button
                className="w-full text-left px-4 py-2 hover:bg-slate-700 flex items-center gap-2"
                onClick={() =>
                  handleCopyMessage(
                    contextMenu.messageId,
                    contextMenu.messageText
                  )
                }
              >
                <FaCopy className="w-4 h-4" />
                {t("psychologist.copy")}
              </button>
              {isMobileDevice() && (
                <button
                  className="w-full text-left px-4 py-2 hover:bg-slate-700 flex items-center gap-2"
                  onClick={() => {
                    setTextSelectionModal({
                      visible: true,
                      messageId: contextMenu.messageId,
                      messageText: contextMenu.messageText,
                    });
                    setContextMenu({
                      visible: false,
                      x: 0,
                      y: 0,
                      messageId: null,
                      messageText: "",
                    });
                  }}
                >
                  <FaItalic className="w-4 h-4" />
                  {t("psychologist.select_text")}
                </button>
              )}
            </>
          )}
          {/* Regenerate button in context menu for AI messages */}
          {(() => {
            const currentMessage = messages.find(
              (msg) => msg.id === contextMenu.messageId
            );
            const currentMessageIndex = messages.findIndex(
              (msg) => msg.id === contextMenu.messageId
            );

            // Знаходимо індекс останнього AI повідомлення
            const lastAiMessageIndex = messages
              .map((msg, index) => ({ ...msg, index }))
              .filter((msg) => msg.sender === "ai")
              .pop()?.index;

            // Показуємо кнопку тільки якщо це AI повідомлення і воно є останнім AI повідомленням
            const isLastAiMessage =
              currentMessage?.sender === "ai" &&
              currentMessageIndex === lastAiMessageIndex;

            return isLastAiMessage;
          })() && (
            <button
              className={`w-full text-left px-4 py-2 hover:bg-slate-700 flex items-center gap-2 ${
                !canAffordAction() || isLoading ? "text-gray-500" : ""
              }`}
              onClick={() => {
                if (!canAffordAction()) {
                  setShowPremium(true);
                } else {
                  handleRegenerateResponse(contextMenu.messageId);
                }
                setContextMenu({
                  visible: false,
                  x: 0,
                  y: 0,
                  messageId: null,
                  messageText: "",
                });
              }}
              disabled={isLoading || !canAffordAction()}
            >
              <FaArrowRotateLeft className="w-4 h-4" />
              {t("psychologist.regenerate")}
            </button>
          )}
          {/* Edit button only for the last user message and not test result messages */}
          {messages.find((msg) => msg.id === contextMenu.messageId)?.sender ===
            "user" &&
            contextMenu.messageId === lastUserMessageId &&
            !contextMenu.messageText.includes("[test-result]") && (
              <button
                className={`w-full text-left px-4 py-2 hover:bg-slate-700 flex items-center gap-2 ${
                  !canAffordAction() || isLoading ? "text-gray-500" : ""
                }`}
                onClick={() => {
                  if (!canAffordAction()) {
                    setShowPremium(true);
                  } else {
                    handleEditMessage(
                      contextMenu.messageId,
                      contextMenu.messageText
                    );
                  }
                  setContextMenu({
                    visible: false,
                    x: 0,
                    y: 0,
                    messageId: null,
                    messageText: "",
                  });
                }}
                disabled={isLoading || !canAffordAction()}
              >
                <FaPencil className="w-4 h-4" />
                {t("psychologist.edit")}
              </button>
            )}
          {/* Delete button only for user messages */}
          {messages.find((msg) => msg.id === contextMenu.messageId)?.sender ===
            "user" && (
            <button
              className="w-full text-left px-4 py-2 hover:bg-slate-700 flex items-center gap-2 text-red-400"
              onClick={() => handleDeleteMessage(contextMenu.messageId)}
            >
              <FaTrash className="w-4 h-4" />
              {t("psychologist.delete")}
            </button>
          )}
        </div>
      )}

      {/* Верхній блок навігації */}
      {/* ОБГОРТКА */}
      <div className="-mx-4 px-4 pt-11 pb-3 bg-gray-950 relative z-40 border-b border-white/[0.05]">
        
        {/* ВЕРХНІЙ РЯДОК: Лічильник (по центру) */}
        <div className="flex justify-center items-center h-7 mb-3">
          <div className="flex items-center gap-2 h-full">
            <FaBrain className="w-5 h-5 text-white shrink-0" />
            {isPointsLoading ? (
              <div className="h-6 w-10 animate-pulse bg-white/20 rounded-md" />
            ) : (
              <span className="text-xl font-bold leading-none text-white">{points}</span>
            )}
          </div>
        </div>

        {/* НИЖНІЙ РЯДОК: Вибір моделі + Меню */}
        <div className="flex justify-between items-center gap-3 w-full">
          
          {/* Дропдаун вибору моделі (flex-1 дозволяє кнопці зайняти весь вільний простір) */}
          <div className="relative" ref={dropdownRef}>
            <button
              className={`flex items-center justify-between gap-2 px-4 py-2 rounded-2xl text-sm font-medium transition-all duration-300 w-full shadow-sm overflow-hidden ${
                isOpen
                  ? "bg-white/15 border-white/20 text-white"
                  : "bg-white/[0.05] border border-white/[0.08] hover:bg-white/10 hover:border-white/15 text-white/90"
              }`}
              onClick={() => {
                setIsOpen(!isOpen);
                setIsMenuOpen(false);
              }}
            >
              <div className="flex items-center gap-2.5 truncate">
                <FaAtom className="w-4 h-4 text-emerald-400 shrink-0" />
                <span className="truncate tracking-wide">{selectedModel}</span>
              </div>
            </button>

            {/* Випадаюче меню моделей */}
            {isOpen && (
              <div className="absolute top-full left-0 mt-2 bg-gray-900/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl w-full min-w-[260px] z-50 overflow-hidden p-1.5 animate-in fade-in slide-in-from-top-2 duration-200">
                {models.map(([key, model]) => {
                  const isSelected = selectedModel === key;
                  return (
                    <div
                      key={key}
                      className={`flex flex-col gap-1 text-start p-3 rounded-xl cursor-pointer transition-colors ${
                        isSelected 
                          ? "bg-emerald-500/15 border border-emerald-500/20" 
                          : "border border-transparent hover:bg-white/5"
                      }`}
                      onClick={() => handleSelect(key, model.premium, model.price)}
                    >
                      <p className={`font-semibold flex items-center text-sm ${isSelected ? "text-emerald-400" : "text-white/90"}`}>
                        {model.premium && (
                          <FaCrown className="w-3.5 h-3.5 me-1.5 text-amber-500 inline drop-shadow-sm" />
                        )}
                        {key}
                      </p>
                      <p className="text-xs text-white/50 leading-tight">
                        {model.description[i18n.language]}
                      </p>
                      
                      <div className="flex justify-between items-center mt-1.5">
                        <div className="flex gap-2 items-center">
                          <span className="flex items-center text-xs font-semibold text-white/80">
                            <FaBrain className="w-3.5 h-3.5 me-1.5 text-white/50" />
                            {model.price}
                          </span>
                          {model.free_with_premium && (
                            <span className="px-2 py-0.5 text-[10px] font-medium flex items-center rounded-md bg-amber-500/10 border border-amber-500/20 text-amber-400">
                              <FaCrown className="w-2.5 h-2.5 me-1" />
                              {t("psychologist.free_with_premium")}
                            </span>
                          )}
                        </div>
                        <span className="text-xs">{renderStars(model.quality)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Дропдаун додаткового меню (Ellipsis) */}
          <div className="relative shrink-0" ref={menuRef}>
            <button
              className={`p-2 rounded-2xl transition-all duration-300 ${
                isMenuOpen 
                  ? "bg-white/15 text-white" 
                  : "bg-white/[0.03] border border-white/[0.05] text-white/60 hover:text-white/90 hover:bg-white/10"
              }`}
              onClick={() => {
                setIsMenuOpen(!isMenuOpen);
                setIsOpen(false);
              }}
            >
              <FaEllipsis className="w-5 h-5" />
            </button>

            {/* Випадаюче меню опцій */}
            {isMenuOpen && (
              <div className="absolute top-full right-0 mt-2 bg-gray-900/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl w-60 z-50 overflow-hidden p-1.5 animate-in fade-in slide-in-from-top-2 duration-200">
                <button
                  className="flex items-center w-full text-left px-3 py-2.5 rounded-xl text-sm font-medium text-red-400 hover:bg-red-500/15 hover:text-red-300 transition-colors"
                  onClick={() => openDialog("ai")}
                >
                  <FaTrash className="w-3.5 h-3.5 me-2 shrink-0" />
                  <span className="truncate">{t("psychologist.remove_chat_for_ai")}</span>
                </button>
                <button
                  className="flex items-center w-full text-left px-3 py-2.5 rounded-xl text-sm font-medium text-red-400 hover:bg-red-500/15 hover:text-red-300 transition-colors mt-0.5"
                  onClick={() => openDialog("full")}
                >
                  <FaTrash className="w-3.5 h-3.5 me-2 shrink-0" />
                  <span className="truncate">{t("psychologist.remove_chat_full")}</span>
                </button>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Блок повідомлень */}
      <div
        ref={chatContainerRef}
        className="bg-gray-950 -mx-4 px-3 flex-1 flex flex-col gap-y-4 py-4 overflow-y-auto text-start hidden-scrollbar lg:custom-scrollbar relative"
        onScroll={handleScroll}
      >
        {isChatLoading ? (
          <div className="flex flex-col gap-6 animate-pulse">
            <div className="bg-slate-800 rounded-lg w-4/5 ms-auto justify-end h-32" />
            <div className="">
              {[...Array(4)].map((_, index) => (
                <Fragment key={index}>
                  <div className="bg-gray-200 rounded-lg py-2 w-full me-16 mb-2" />
                  <div className="bg-gray-200 rounded-lg py-2 w-4/5 mb-2" />
                  <div className="bg-gray-200 rounded-lg py-2 w-3/4 mb-2" />
                  <div className="bg-gray-200 rounded-lg py-2 w-3/5 mb-6" />
                </Fragment>
              ))}
            </div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col gap-4 justify-center items-center h-full text-gray-400 text-center">
            <FaMessage className="w-20 h-20" />
            <p className="w-3/4">{t("psychologist.noMessages")}</p>
          </div>
        ) : (
          messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${
                message.sender === "user" ? "justify-end" : "justify-start"
              } ${message.isLoading ? "animate-fade-in" : ""}`}
            >
              <div
                className={`relative group ${
                  message.sender === "user" ? "ms-16" : "me-5"
                } ${
                  longPressActive === message.id
                    ? "animate-pulse bg-gray-800/20 rounded-lg"
                    : ""
                }`}
                onTouchStart={(e) =>
                  handleTouchStart(e, message.id, message.text)
                }
                onTouchEnd={handleTouchEnd}
                onTouchMove={handleTouchMove}
                onMouseDown={(e) =>
                  handleMouseDown(e, message.id, message.text)
                }
                onContextMenu={(e) => e.preventDefault()}
              >
                {/* Message content */}
                {editingMessageId === message.id ? (
                  <div className="flex flex-col gap-2">
                    <textarea
                      className={`${
                        message.sender === "user"
                          ? "bg-slate-800"
                          : "bg-gray-800"
                      } text-white px-3 py-1 rounded-lg whitespace-pre-wrap resize-none w-72 max-w-full min-h-24`}
                      value={editingText}
                      onChange={(e) => setEditingText(e.target.value)}
                      rows={Math.max(2, editingText.split("\n").length + 1)}
                      style={{
                        lineHeight: "1.5",
                        fontSize: "inherit",
                      }}
                      autoFocus
                    />
                    <div className="flex gap-2 justify-end">
                      <button
                        className={`px-3 py-1 rounded text-sm ${
                          isSaveButtonActive()
                            ? "bg-emerald-600 hover:bg-emerald-700"
                            : "bg-gray-600 cursor-not-allowed"
                        }`}
                        onClick={handleSaveEdit}
                      >
                        {t("common.save")}
                      </button>
                      <button
                        className="px-3 py-1 bg-slate-600 hover:bg-slate-700 rounded text-sm"
                        onClick={handleCancelEdit}
                      >
                        {t("common.cancel")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    {message.text.includes("[test-result]") ? (
                      <div className="bg-gradient-to-r from-cyan-900/50 to-emerald-900/50 border border-cyan-500/30 rounded-lg px-4 py-3 shadow-lg select-none">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-2 h-2 bg-cyan-400 rounded-full"></div>
                          <span className="text-cyan-300 text-sm font-medium">
                            {t("psychologist.testResultTitle")}
                          </span>
                        </div>
                        <p className="text-white text-base leading-relaxed">
                          {message.text.replace(/\[\/?test-result\]/g, "")}
                        </p>
                      </div>
                    ) : (
                      <p
                        className={`px-3 py-1 inline-block rounded-lg whitespace-pre-wrap cursor-pointer select-none break-words overflow-hidden ${
                          message.sender === "user"
                            ? "bg-slate-800"
                            : "text-gray-200"
                        } ${message.isLoading ? "animate-slide-up" : ""}`}
                        style={{
                          userSelect: "none",
                          WebkitUserSelect: "none",
                          MozUserSelect: "none",
                          msUserSelect: "none",
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          maxWidth: "100%",
                        }}
                      >
                        {message.isLoading ? (
                          <div className="flex items-center gap-2">
                            <div className="flex gap-1">
                              <div
                                className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                                style={{ animationDelay: "0ms" }}
                              ></div>
                              <div
                                className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                                style={{ animationDelay: "150ms" }}
                              ></div>
                              <div
                                className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                                style={{ animationDelay: "300ms" }}
                              ></div>
                            </div>
                            <span className="text-gray-400 text-sm">
                              {t("psychologist.testAnalysisLoading")}
                            </span>
                          </div>
                        ) : (
                          <div>
                            {(() => {
                              const messageText =
                                message.sender === "ai"
                                  ? getCurrentVariantText(message.id)
                                  : message.text;

                              // Remove [test-result] tags from AI messages (only user can send this)
                              let processedText = messageText;
                              if (message.sender === "ai") {
                                processedText = messageText
                                  .replace(
                                    /\[\/?test-result\][^\]]*\[?\/?test-result\]?/g,
                                    ""
                                  )
                                  .trim();
                              }

                              // Extract all [practice] blocks recommended by AI
                              const { practices, cleanedText, hasMalformed } =
                                parsePracticeBlocks(processedText);

                              // Check for [test]id=*id*, name=*name*[/test] pattern
                              const testLinkMatch = cleanedText.match(
                                /\[test\]id=([^,]+),\s*name=([^\]]+)\[\/test\]/
                              );

                              if (
                                practices.length === 0 &&
                                !hasMalformed &&
                                !testLinkMatch
                              ) {
                                return highlightHeadings(processedText);
                              }

                              const testId = testLinkMatch?.[1]?.trim();
                              const testName = testLinkMatch?.[2]?.trim();
                              const hasValidTest =
                                testLinkMatch && testId && testName;

                              const textWithoutTags = cleanedText
                                .replace(/\[\/?test\][^\]]*\[?\/?test\]?/g, "")
                                .trim();

                              const renderPracticeCard = (p, idx) => {
                                const practiceData = practicesMap[p.id];
                                const isGuided = p.type === "guided";
                                const typeLabel = isGuided
                                  ? t("psychologist.guidedMeditation")
                                  : t("psychologist.breathingExercise");
                                const Icon = isGuided ? FaSpa : FaWind;
                                const accent = isGuided
                                  ? {
                                      card: "from-violet-900/50 to-indigo-900/50",
                                      border: "border-violet-500/30",
                                      text: "text-violet-300",
                                      btn: "from-violet-500 to-indigo-500 hover:from-violet-600 hover:to-indigo-600",
                                    }
                                  : {
                                      card: "from-cyan-900/50 to-teal-900/50",
                                      border: "border-cyan-500/30",
                                      text: "text-cyan-300",
                                      btn: "from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600",
                                    };

                                return (
                                  <div
                                    key={`practice-${idx}`}
                                    className={`bg-gradient-to-br ${accent.card} border ${accent.border} rounded-lg px-4 py-3 shadow-lg select-none`}
                                  >
                                    <div className="flex items-center gap-2 mb-1.5">
                                      <Icon
                                        className={`w-4 h-4 ${accent.text} shrink-0`}
                                      />
                                      <span
                                        className={`${accent.text} text-xs font-semibold uppercase tracking-wide`}
                                      >
                                        {typeLabel}
                                      </span>
                                      {practiceData?.isPremium && (
                                        <FaCrown className="w-3.5 h-3.5 ms-auto text-amber-500" />
                                      )}
                                    </div>
                                    <p className="text-white text-base font-semibold leading-snug mb-1 break-words">
                                      {p.name}
                                    </p>
                                    {practiceData &&
                                      (practiceData.duration ||
                                        practiceData.totalDurationMinutes) && (
                                        <p className="text-white/50 text-xs mb-2.5 flex items-center gap-1">
                                          <FaRegClock className="w-3 h-3" />
                                          {practiceData.duration ||
                                            practiceData.totalDurationMinutes}{" "}
                                          {t("meditation.min")}
                                        </p>
                                      )}
                                    <button
                                      onClick={() => handleStartPractice(p)}
                                      className={`w-full px-4 py-2 bg-gradient-to-r ${accent.btn} text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition`}
                                    >
                                      <FaPlay className="w-3.5 h-3.5" />
                                      {t("psychologist.startPractice")}
                                    </button>
                                  </div>
                                );
                              };

                              return (
                                <div>
                                  {highlightHeadings(textWithoutTags)}
                                  {practices.length > 0 && (
                                    <div className="mt-3 flex flex-col gap-2">
                                      {practices.map(renderPracticeCard)}
                                    </div>
                                  )}
                                  {hasMalformed && (
                                    <div className="mt-3">
                                      <div className="bg-red-900/50 border border-red-500/30 rounded-lg px-4 py-3">
                                        <div className="flex items-center gap-2 mb-1">
                                          <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                                          <span className="text-red-300 text-sm font-medium">
                                            {t("psychologist.practiceFormatError")}
                                          </span>
                                        </div>
                                        <p className="text-red-200 text-sm">
                                          {t("psychologist.practiceLinkError")}
                                        </p>
                                      </div>
                                    </div>
                                  )}
                                  {hasValidTest && (
                                    <div className="mt-3">
                                      <div className="text-center mb-2">
                                        <span className="text-emerald-400 font-medium">
                                          {testName}
                                        </span>
                                      </div>
                                      <Link
                                        to={`/test/${testId}`}
                                        className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-lg flex w-full items-center gap-2 hover:from-emerald-600 hover:to-cyan-600 transition"
                                      >
                                        <FaPlay className="w-4 h-4" />
                                        {t("psychologist.startTest")}
                                      </Link>
                                    </div>
                                  )}
                                  {testLinkMatch && !hasValidTest && (
                                    <div className="mt-3">
                                      <div className="bg-red-900/50 border border-red-500/30 rounded-lg px-4 py-3">
                                        <div className="flex items-center gap-2 mb-1">
                                          <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                                          <span className="text-red-300 text-sm font-medium">
                                            {t("psychologist.testFormatError")}
                                          </span>
                                        </div>
                                        <p className="text-red-200 text-sm">
                                          {t("psychologist.testLinkError")}
                                        </p>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        )}
                      </p>
                    )}

                    {/* AI Message Controls */}
                    {message.sender === "ai" &&
                      !message.isLoading &&
                      message.id === lastAiMessageId && (
                        <div className="flex flex-col items-center gap-2 my-2 ml-3">
                          {/* Like/Dislike buttons */}
                          <div className="flex items-center w-full">
                            <button
                              className={`p-2 rounded transition-colors ${
                                messageReactions[message.id] === "like"
                                  ? "text-green-400"
                                  : "text-gray-400 hover:text-white"
                              }`}
                              onClick={() =>
                                handleMessageReaction(message.id, "like")
                              }
                            >
                              <FaThumbsUp className="w-4 h-4" />
                            </button>
                            <button
                              className={`p-2 rounded transition-colors ${
                                messageReactions[message.id] === "dislike"
                                  ? "text-red-400"
                                  : "text-gray-400 hover:text-white"
                              }`}
                              onClick={() =>
                                handleMessageReaction(message.id, "dislike")
                              }
                            >
                              <FaThumbsDown className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}
                  </div>
                )}
              </div>
            </div>
          ))
        )}

        <div ref={messagesEndRef} />

        {/* Scroll to bottom button */}
        {showScrollButton && (
          <button
            onClick={scrollToBottom}
            className="fixed bottom-20 right-6 bg-gray-600/30 hover:bg-gray-600/50 text-gray-300 hover:text-white rounded-full p-2 shadow-sm z-10 transition-all duration-200"
          >
            <FaChevronDown className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Поле вводу */}
      <div className="flex mb-2 -mx-4 bg-gray-950">
        <textarea
          placeholder={t("psychologist.message")}
          className={
            (text ? "rounded-tl-xl" : "rounded-t-xl") +
            " bg-gray-900 border-slate-700 border-t-2 text-lg flex-1 outline-none py-2 ps-4 pe-2 resize-none overflow-x-hidden overflow-y-auto lg:custom-scrollbar placeholder:text-gray-200"
          }
          value={text}
          onChange={(e) => {
            const value = e.target.value;
            setText(value);
            localStorage.setItem("aiPsychologistDraft", value);
          }}
          rows={currentRows}
          style={{ lineHeight: `${lineHeight}px` }}
          disabled={isLoading}
        />
        <div
          className={
            (text ? "rounded-tr-xl" : "rounded-t-xl") +
            " bg-gray-900 border-slate-700 flex items-end border-t-2"
          }
        >
          {text.trim().length > 0 && canAffordAction() && (
            <IoSend
              className={`text-emerald-600 w-6 h-6 ms-2 me-4 my-2 cursor-pointer ${
                isLoading || !canAffordAction()
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
              onClick={handleSendMessage}
            />
          )}

          {/* Якщо points < modelPrice, кнопка неактивна */}
          {text.trim().length > 0 && !canAffordAction() && (
            <IoSend
              className="text-gray-500 w-6 h-6 ms-2 me-4 my-2 cursor-not-allowed"
              onClick={handleSendMessage}
            />
          )}
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes bounce-slow {
          0%,
          20%,
          50%,
          80%,
          100% {
            transform: translateY(0);
          }
          40% {
            transform: translateY(-10px);
          }
          60% {
            transform: translateY(-5px);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }

        .animate-slide-up {
          animation: slide-up 0.4s ease-out;
        }

        .animate-bounce-slow {
          animation: bounce-slow 2s infinite;
        }
      `}</style>
    </div>
  );
}

export default AiPsychologist;
